const ED25519_ALGORITHM = { name: 'Ed25519' } as const;
const KEY_USAGES: ReadonlyArray<KeyUsage> = ['sign', 'verify'];
const ED25519_KEY_BYTES = 32;

// An Ed25519 PKCS#8 export is a fixed 16-byte DER prefix
// (30 2e 02 01 00 30 05 06 03 2b 65 70 04 22 04 20) followed by the 32-byte
// private seed. Web Crypto offers no `raw` export for the private key, so the
// seed is recovered by slicing off this prefix.
const ED25519_PKCS8_PREFIX = Uint8Array.from([
  0x30, 0x2e, 0x02, 0x01, 0x00, 0x30, 0x05, 0x06, 0x03, 0x2b, 0x65, 0x70, 0x04,
  0x22, 0x04, 0x20,
]);
const ED25519_PKCS8_BYTES = ED25519_PKCS8_PREFIX.length + ED25519_KEY_BYTES;

let supportProbe: Promise<boolean> | undefined;

export class KeygenError extends Error {
  // `true` when the browser fundamentally can't do Ed25519
  readonly unsupported: boolean;

  constructor(
    message: string,
    options?: { cause?: unknown; unsupported?: boolean },
  ) {
    super(message, options);
    this.name = 'KeygenError';
    this.unsupported = options?.unsupported ?? false;
  }
}

// A capability gap appears as:
// - `TypeError` (missing `crypto.subtle`)
// - `NotSupportedError` (algorithm not registered);
// Anything else (e.g.`OperationError`) is a one-time failure
const isUnsupportedCause = (cause: unknown): boolean => {
  if (cause instanceof TypeError) {
    return true;
  }
  // Real Web Crypto rejects with a `DOMException`, which subclasses `Error`
  // on our target engines — but key on `.name` directly so this doesn't
  // depend on that subclassing relationship holding.
  return (
    typeof cause === 'object' &&
    cause !== null &&
    'name' in cause &&
    cause.name === 'NotSupportedError'
  );
};

export type Ed25519Keypair = {
  publicKey: Uint8Array;
  // Raw 32-byte private seed
  privateKeySeed: Uint8Array;
};

// Memoised — the underlying `generateKey` probe runs at most once per page load.
export const isEd25519Supported = (): Promise<boolean> => {
  if (!supportProbe) {
    supportProbe = (async () => {
      try {
        await globalThis.crypto.subtle.generateKey(ED25519_ALGORITHM, true, [
          ...KEY_USAGES,
        ]);
        return true;
      } catch (cause) {
        return !isUnsupportedCause(cause);
      }
    })();
  }
  return supportProbe;
};

export const generateEd25519Keypair = async (): Promise<Ed25519Keypair> => {
  try {
    // Ed25519 with sign/verify usages always yields a CryptoKeyPair, but the
    // Web Crypto types widen the return to `CryptoKey | CryptoKeyPair`.
    const keyPair = (await globalThis.crypto.subtle.generateKey(
      ED25519_ALGORITHM,
      true,
      [...KEY_USAGES],
    )) as CryptoKeyPair;
    const [publicKeyBuffer, privateKeyBuffer] = await Promise.all([
      globalThis.crypto.subtle.exportKey('raw', keyPair.publicKey),
      globalThis.crypto.subtle.exportKey('pkcs8', keyPair.privateKey),
    ]);
    // The PKCS#8 export contains the raw seed; copy out just the seed, then zero
    // the full buffer so the only lingering copy is the 32-byte result.
    const pkcs8 = new Uint8Array(privateKeyBuffer);
    let privateKeySeed: Uint8Array;
    try {
      privateKeySeed = extractEd25519Seed(pkcs8);
    } finally {
      pkcs8.fill(0);
    }
    return {
      publicKey: new Uint8Array(publicKeyBuffer),
      privateKeySeed,
    };
  } catch (cause) {
    if (cause instanceof KeygenError) {
      throw cause;
    }
    throw new KeygenError('Failed to generate Ed25519 keypair', {
      cause,
      unsupported: isUnsupportedCause(cause),
    });
  }
};

// Validates length + prefix before slicing — this is key material, so a
// malformed export must fail loudly rather than yield wrong-but-plausible bytes.
const extractEd25519Seed = (pkcs8: Uint8Array): Uint8Array => {
  if (pkcs8.length !== ED25519_PKCS8_BYTES) {
    throw new KeygenError(
      `Unexpected PKCS#8 length: ${pkcs8.length} (expected ${ED25519_PKCS8_BYTES})`,
    );
  }
  for (let i = 0; i < ED25519_PKCS8_PREFIX.length; i += 1) {
    if (pkcs8[i] !== ED25519_PKCS8_PREFIX[i]) {
      throw new KeygenError('Unexpected PKCS#8 prefix for Ed25519 private key');
    }
  }
  return pkcs8.slice(ED25519_PKCS8_PREFIX.length);
};

export const ed25519KeyToHex = (key: Uint8Array): string => {
  if (key.length !== ED25519_KEY_BYTES) {
    throw new KeygenError(
      `Expected a ${ED25519_KEY_BYTES}-byte key, got ${key.length}`,
    );
  }
  let hex = '';
  for (const byte of key) {
    hex += byte.toString(16).padStart(2, '0');
  }
  return hex;
};
